import { Component } from '@angular/core';
import { BehaviorSubject, combineLatest, distinctUntilChanged, map, tap } from 'rxjs';
import {
  CreditsAlert,
  CreditsBalanceService,
} from '@core/services/credits-balance.service';
import { LoggerService } from '@core/services/logger.service';
import { SessionService } from '@core/services/session.service';
import { environment } from '@env';
import { LogEvent } from '@core/enums/log-event';
import { CreditsModalLauncherService } from '../../services/credits-modal-launcher.service';

@Component({
  selector: 'app-whatsapp-credits-alert',
  templateUrl: './whatsapp-credits-alert.component.html',
  styleUrls: ['./whatsapp-credits-alert.component.scss'],
})
export class WhatsappCreditsAlertComponent {
  private readonly dismissed$ = new BehaviorSubject<boolean>(false);
  private lastLoggedAlertKey: string | null = null;

  /** Alert view model gated by the session-only dismiss flag. */
  protected readonly vm$ = combineLatest([
    this.creditsBalanceService.alert$,
    this.dismissed$,
  ]).pipe(
    map(([alert, dismissed]) => ({ ...alert, show: alert.show && !dismissed })),
    tap((vm) => this.logAlertShown(vm))
  );

  protected readonly creditsStatementUrl = `${environment.externalUrls.platform}/whatsapp/credits-statement`;

  /**
   * v2 "Comprar créditos": buying lives in the admin, which only managers can open.
   * Derived from the session stream instead of read in the template: this banner sits
   * in app.component, so a template read would re-parse the whole stored session on
   * every change detection. Before login there is no user yet, which reads as false.
   */
  protected readonly canManageCredits$ = this.sessionService.session$.pipe(
    map((session) => {
      const roles = session?.user?.userRole ?? [];
      return roles.includes('MANAGER') || roles.includes('SUPER_ADMIN');
    }),
    distinctUntilChanged()
  );

  constructor(
    private creditsBalanceService: CreditsBalanceService,
    private creditsModalLauncher: CreditsModalLauncherService,
    private logger: LoggerService,
    private sessionService: SessionService
  ) {}

  open(): void {
    this.creditsModalLauncher.open('banner');
  }

  /** Closes the banner for the current session only (no persistence). */
  dismiss(event: MouseEvent): void {
    event.stopPropagation();
    this.dismissed$.next(true);
  }

  /**
   * Fires LIVECHAT_CREDITS_ALERT_SHOWN once per visible banner state — not on
   * every 5 min poll — so Datadog counts reflect how often each warning/danger
   * banner was actually shown. Hiding the banner resets the guard, so it logs
   * again if the same state reappears later in the session.
   */
  private logAlertShown(vm: CreditsAlert): void {
    if (!vm.show) {
      this.lastLoggedAlertKey = null;
      return;
    }

    const key = `${vm.variation}|${vm.messageKey}|${vm.count}`;
    if (key === this.lastLoggedAlertKey) return;
    this.lastLoggedAlertKey = key;

    this.logger.log(LogEvent.LIVECHAT_CREDITS_ALERT_SHOWN, {
      variation: vm.variation,
      worstLevel: vm.worstLevel,
      count: vm.count,
      messageKey: vm.messageKey,
      companyName: vm.companyName,
    });
  }
}
